import { useCallback, useContext, useMemo } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { useIntl } from 'react-intl'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { sortByDate } from 'components/Table/utils'
import {
  Table,
  DownloadButtonCell,
  DeleteButtonCell,
  DateCell,
} from 'components/Table'
import { Regular } from 'components/Typography'
import type { Template } from 'features/templates/types'
import NameCell from './NameCell'
import { StyledTableOuterWrapper, StyledTableInnerWrapper } from './styles'
import { deleteTemplateThunk } from 'features/templates/slice'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import type { TableHeader } from 'components/Table/types'
import { TemplatesMobileBlock } from 'features/templates/components/TemplatesMobileBlock'
import { templatesPageMessages } from 'features/templates/messages'
import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'

interface TemplatesTableProps {
  data: Template[]
  searchValue?: string
  headers: Record<string, TableHeader>
  defaultSortBy: SortingRule<Template>
  sortBy: SortingRule<Template>
  setSortBy: (sortBy: SortingRule<Template>) => void
}

function TemplatesTable({
  data,
  searchValue,
  headers,
  defaultSortBy,
  sortBy,
  setSortBy,
}: TemplatesTableProps) {
  const { hasRootAdminPermission } = useContext(CurrentUserPermissionsContext)

  const hiddenColumns = useMemo(
    () => (hasRootAdminPermission ? [] : [headers.DeleteButton.id]),
    [hasRootAdminPermission, headers.DeleteButton.id]
  )

  const nameCell = (cell: Cell) => <NameCell name={cell.value} />

  const DownloadButton = (cell: Cell<Template>) => {
    const template = cell.row.original
    const { name, downloadLink } = template
    const fileName = `${name}.tar.gz`

    return <DownloadButtonCell url={downloadLink} fileName={fileName} />
  }

  const DeleteButton = (cell: Cell<Template>) => {
    const dispatch = useAppDispatch()
    const { formatMessage } = useIntl()
    const template = cell.row.original
    const { name, id } = template
    const rootDomainId = useAppSelector(currentRootDomainIdSelector)

    const handleDelete = useCallback(async () => {
      if (rootDomainId) {
        await dispatch(deleteTemplateThunk(rootDomainId, id))
      }
    }, [dispatch, id, rootDomainId])

    return (
      <DeleteButtonCell
        title={formatMessage(templatesPageMessages.deleteTemplateModalTitle)}
        handleConfirm={handleDelete}
        disabled={!rootDomainId}
      >
        <Regular>
          {formatMessage(templatesPageMessages.deleteTemplateModalDescription, {
            name,
            br: <br />,
          })}
        </Regular>
      </DeleteButtonCell>
    )
  }

  const columns: readonly Column<Template>[] = useMemo(
    () => [
      {
        id: headers.name.id,
        Header: headers.name.label,
        accessor: (row) => row.name,
        Cell: nameCell,
        percentWidth: '30%',
      },
      {
        id: headers.uploaded.id,
        Header: headers.uploaded.label,
        accessor: (row) => row.uploaded,
        percentWidth: '20%',
        Cell: DateCell,
        sortType: sortByDate,
        disableGlobalFilter: true,
      },
      {
        id: headers.integratorName.id,
        Header: headers.integratorName.label,
        accessor: (row) => row.integratorName,
        percentWidth: '20%',
      },
      {
        id: headers.version.id,
        Header: headers.version.label,
        accessor: (row) => row.version,
        percentWidth: '5%',
        disableGlobalFilter: true,
      },
      {
        Header: '',
        id: headers.downloadLink.id,
        accessor: (row) => row.downloadLink,
        Cell: DownloadButton,
        disableSortBy: true,
        disableGlobalFilter: true,
        percentWidth: '5%',
      },
      {
        Header: '',
        id: headers.DeleteButton.id,
        accessor: (row) => row.id,
        Cell: DeleteButton,
        disableSortBy: true,
        disableGlobalFilter: true,
        percentWidth: '5%',
      },
    ],
    [headers]
  )

  return (
    <StyledTableOuterWrapper>
      <StyledTableInnerWrapper>
        <Table
          columns={columns}
          data={data}
          searchValue={searchValue}
          defaultSortBy={defaultSortBy}
          sortBy={sortBy}
          setSortBy={setSortBy}
          hiddenColumns={hiddenColumns}
          mobileViewComponent={TemplatesMobileBlock}
        />
      </StyledTableInnerWrapper>
    </StyledTableOuterWrapper>
  )
}

export default TemplatesTable
